/**
 * Helper functions for processing passage text and HTML
 * Used by ListeningTestUI and ListeningTestUI2
 */

const READING_HIGHLIGHT_CLASS = 'ielts-reading-note-highlight';
const HIGHLIGHT_TEXT_COLOR = '#2b2200';
const HIGHLIGHT_BACKGROUND = '#ffe98a';
const NOTE_UNDERLINE = '#d6a900';
const readingHighlightState = new Map();
let readingHighlightController = null;
let pendingReadingSelection = null;
let suppressReadingDocumentClicksUntil = 0;

/**
 * Find question number from previous siblings (including inside elements)
 */
export function findQuestionNumberFromSiblings(node) {
  while (node) {
    if (node.nodeType === 3) {
      const match = node.textContent.match(/<(\d+)>/);
      if (match) return match[1];
    } else if (node.nodeType === 1) {
      const match = node.textContent.match(/<(\d+)>/);
      if (match) return match[1];
    }
    node = node.previousSibling;
  }
  return null;
}

/**
 * Clean angle bracket markers from HTML (used for question-box content)
 */
function cleanAngleBrackets(html) {
  if (!html) return html;
  return html
    .replace(/(&lt;(\d+)&gt;)/g, '')
    .replace(/(<(\d+)>)/g, '');
}

function getReadingHighlightKey(container) {
  const passageText = container?.textContent?.replace(/\s+/g, ' ').trim() || '';
  return passageText ? `ielts-reading:${passageText.slice(0, 120)}:${passageText.length}` : 'ielts-reading:empty';
}

function getReadingPassageContainer() {
  return document.querySelector('.ielts-passage-html');
}

function getTextNodes(root, options = {}) {
  const { includeHighlights = false } = options;
  const nodes = [];
  if (!root) return nodes;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      if (!includeHighlights && node.parentElement?.closest(`.${READING_HIGHLIGHT_CLASS}`)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  while (walker.nextNode()) {
    nodes.push(walker.currentNode);
  }
  return nodes;
}

function getRangeOffsets(container, range) {
  const nodes = getTextNodes(container, { includeHighlights: true });
  let offset = 0;
  let start = null;
  let end = null;

  nodes.forEach((node) => {
    const length = node.nodeValue.length;
    if (node === range.startContainer) start = offset + range.startOffset;
    if (node === range.endContainer) end = offset + range.endOffset;
    offset += length;
  });

  if (start === null || end === null || end <= start) return null;
  return { start, end };
}

function findNodeAtOffset(nodes, targetOffset, bias = 'end') {
  let offset = 0;
  for (const node of nodes) {
    const nextOffset = offset + node.nodeValue.length;
    const isBoundary = targetOffset === offset;

    if (
      targetOffset < nextOffset
      || (targetOffset === nextOffset && bias === 'end')
      || (isBoundary && bias === 'start')
    ) {
      return { node, offset: Math.max(0, targetOffset - offset) };
    }
    offset = nextOffset;
  }
  const last = nodes[nodes.length - 1];
  return last ? { node: last, offset: last.nodeValue.length } : null;
}

function unwrapExistingHighlights(container) {
  container.querySelectorAll(`.${READING_HIGHLIGHT_CLASS}`).forEach((highlight) => {
    highlight.replaceWith(document.createTextNode(highlight.textContent || ''));
  });
  container.normalize();
}

function renderReadingHighlights(container, key) {
  const highlights = readingHighlightState.get(key) || [];
  unwrapExistingHighlights(container);

  [...highlights]
    .sort((a, b) => b.start - a.start)
    .forEach((highlight) => {
      const nodes = getTextNodes(container);
      const startPoint = findNodeAtOffset(nodes, highlight.start, 'start');
      const endPoint = findNodeAtOffset(nodes, highlight.end, 'end');
      if (!startPoint || !endPoint) return;

      const range = document.createRange();
      try {
        range.setStart(startPoint.node, startPoint.offset);
        range.setEnd(endPoint.node, endPoint.offset);
      } catch (error) {
        return;
      }

      const mark = document.createElement('mark');
      mark.className = READING_HIGHLIGHT_CLASS;
      mark.dataset.highlightId = highlight.id;
      mark.dataset.highlightType = highlight.type || 'marker';
      mark.title = highlight.note ? `Note: ${highlight.note}` : 'Click for options';
      mark.style.backgroundColor = HIGHLIGHT_BACKGROUND;
      mark.style.color = HIGHLIGHT_TEXT_COLOR;
      mark.style.borderRadius = '3px';
      mark.style.padding = '0 2px';
      mark.style.cursor = 'pointer';
      mark.style.boxShadow = highlight.type === 'note' ? `inset 0 -2px 0 ${NOTE_UNDERLINE}` : 'none';

      try {
        range.surroundContents(mark);
      } catch (error) {
        const text = range.toString();
        mark.textContent = text;
        range.deleteContents();
        range.insertNode(mark);
      }
    });
}

function closeReadingFloatingControls() {
  const toolbar = document.getElementById('ielts-reading-selection-toolbar');
  const popover = document.getElementById('ielts-reading-note-popover');
  if (toolbar) toolbar.remove();
  if (popover) popover.remove();
}

function createFloatingShell(id, anchorRect, width = 260) {
  const shell = document.createElement('div');
  shell.id = id;
  shell.style.position = 'fixed';
  shell.style.zIndex = '10000';
  shell.style.width = `${width}px`;
  shell.style.padding = '10px';
  shell.style.border = '1px solid #d8d2c0';
  shell.style.borderRadius = '8px';
  shell.style.background = '#fffdf5';
  shell.style.boxShadow = '0 10px 30px rgba(0,0,0,0.16)';
  shell.style.fontFamily = 'inherit';

  const left = Math.min(anchorRect.left, window.innerWidth - width - 16);
  const top = anchorRect.bottom + 8 > window.innerHeight - 140 ? anchorRect.top - 120 : anchorRect.bottom + 8;
  shell.style.left = `${Math.max(8, left)}px`;
  shell.style.top = `${Math.max(8, top)}px`;

  shell.addEventListener('pointerdown', (event) => event.stopPropagation());
  shell.addEventListener('click', (event) => event.stopPropagation());

  return shell;
}

function createControlButton(label, title, variant = 'neutral') {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.title = title;
  button.style.border = variant === 'primary' ? 'none' : variant === 'danger' ? '1px solid #d9534f' : '1px solid #ddd';
  button.style.borderRadius = '6px';
  button.style.padding = '7px 10px';
  button.style.background = variant === 'primary' ? '#996CFE' : '#fff';
  button.style.color = variant === 'primary' ? '#fff' : variant === 'danger' ? '#d9534f' : '#333';
  button.style.cursor = 'pointer';
  button.style.fontSize = '14px';
  button.style.fontWeight = '600';
  return button;
}

function createToolbarIconButton(iconSvg, title, onPress) {
  const button = document.createElement('button');
  button.type = 'button';
  button.title = title;
  button.setAttribute('aria-label', title);
  button.innerHTML = iconSvg;
  button.style.width = '38px';
  button.style.height = '38px';
  button.style.border = 'none';
  button.style.borderRadius = '999px';
  button.style.background = '#996CFE';
  button.style.color = '#fff';
  button.style.cursor = 'pointer';
  button.style.display = 'inline-flex';
  button.style.alignItems = 'center';
  button.style.justifyContent = 'center';
  button.style.padding = '0';
  button.style.boxShadow = '0 4px 12px rgba(153,108,254,0.28)';

  button.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    event.stopPropagation();
    suppressReadingDocumentClicksUntil = Date.now() + 600;
    onPress();
  });
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  return button;
}

function getMarkerIconSvg() {
  return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M4 17.5L14.8 6.7L17.3 9.2L6.5 20H4V17.5Z" fill="currentColor"/><path d="M16.2 5.3L17.6 3.9C18.1 3.4 18.9 3.4 19.4 3.9L20.1 4.6C20.6 5.1 20.6 5.9 20.1 6.4L18.7 7.8L16.2 5.3Z" fill="currentColor"/><path d="M3.5 21H12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
}

function getNoteIconSvg() {
  return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M6 3.8H15.5L20.2 8.5V20.2H6V3.8Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M15.3 4V8.7H20" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M8.8 12H17" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M8.8 15.5H15" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';
}

function saveReadingSelection(type) {
  const container = getReadingPassageContainer();
  if (!container || !pendingReadingSelection) return null;

  const currentKey = getReadingHighlightKey(container);
  const highlights = readingHighlightState.get(currentKey) || [];
  const highlight = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text: pendingReadingSelection.text,
    note: '',
    type,
    start: pendingReadingSelection.start,
    end: pendingReadingSelection.end,
  };

  readingHighlightState.set(currentKey, [...highlights, highlight]);
  pendingReadingSelection = null;
  window.getSelection()?.removeAllRanges();
  renderReadingHighlights(container, currentKey);
  return { container, currentKey, highlight };
}

function showSelectionToolbar(anchorRect) {
  closeReadingFloatingControls();

  const toolbar = createFloatingShell('ielts-reading-selection-toolbar', anchorRect, 108);
  toolbar.style.display = 'flex';
  toolbar.style.gap = '10px';
  toolbar.style.alignItems = 'center';
  toolbar.style.justifyContent = 'center';
  toolbar.style.padding = '8px';

  const markerButton = createToolbarIconButton(getMarkerIconSvg(), 'Highlight selected text', () => {
    saveReadingSelection('marker');
    closeReadingFloatingControls();
  });

  const noteButton = createToolbarIconButton(getNoteIconSvg(), 'Add note to selected text', () => {
    const saved = saveReadingSelection('note');
    closeReadingFloatingControls();
    if (!saved) return;
    const mark = saved.container.querySelector(`[data-highlight-id="${saved.highlight.id}"]`);
    if (mark) showReadingNotePopover(mark, saved.currentKey, saved.highlight.id);
  });

  toolbar.append(markerButton, noteButton);
  document.body.appendChild(toolbar);
}

function showReadingNotePopover(anchor, key, highlightId) {
  const highlights = readingHighlightState.get(key) || [];
  const highlight = highlights.find((item) => item.id === highlightId);
  if (!highlight) return;

  closeReadingFloatingControls();

  const popover = createFloatingShell('ielts-reading-note-popover', anchor.getBoundingClientRect(), 280);

  const textarea = document.createElement('textarea');
  textarea.value = highlight.note || '';
  textarea.placeholder = 'Write a note for this highlight';
  textarea.style.width = '100%';
  textarea.style.minHeight = '82px';
  textarea.style.resize = 'vertical';
  textarea.style.boxSizing = 'border-box';
  textarea.style.border = '1px solid #c9b66a';
  textarea.style.borderRadius = '6px';
  textarea.style.padding = '8px';
  textarea.style.fontSize = '14px';
  textarea.style.marginBottom = '10px';

  const actions = document.createElement('div');
  actions.style.display = 'flex';
  actions.style.gap = '8px';
  actions.style.justifyContent = 'flex-end';

  const saveButton = createControlButton('Save', 'Save note', 'primary');
  const deleteButton = createControlButton('Delete note', 'Delete note and highlight', 'danger');
  const cancelButton = createControlButton('Cancel', 'Close note editor');

  saveButton.addEventListener('click', () => {
    highlight.note = textarea.value.trim();
    highlight.type = 'note';
    readingHighlightState.set(key, highlights);
    closeReadingFloatingControls();
    const container = getReadingPassageContainer();
    if (container) renderReadingHighlights(container, key);
  });

  deleteButton.addEventListener('click', () => {
    readingHighlightState.set(key, highlights.filter((item) => item.id !== highlightId));
    closeReadingFloatingControls();
    const container = getReadingPassageContainer();
    if (container) renderReadingHighlights(container, key);
  });

  cancelButton.addEventListener('click', closeReadingFloatingControls);

  actions.append(cancelButton, deleteButton, saveButton);
  popover.append(textarea, actions);
  document.body.appendChild(popover);
  textarea.focus();
}

function showMarkerOptionsPopover(anchor, key, highlightId) {
  const highlights = readingHighlightState.get(key) || [];
  const highlight = highlights.find((item) => item.id === highlightId);
  if (!highlight) return;

  closeReadingFloatingControls();

  const popover = createFloatingShell('ielts-reading-note-popover', anchor.getBoundingClientRect(), 230);
  const text = document.createElement('div');
  text.textContent = 'Highlighted text';
  text.style.fontSize = '14px';
  text.style.fontWeight = '600';
  text.style.marginBottom = '10px';
  text.style.color = '#333';

  const actions = document.createElement('div');
  actions.style.display = 'flex';
  actions.style.gap = '8px';
  actions.style.justifyContent = 'flex-end';

  const removeButton = createControlButton('Remove highlight', 'Remove yellow highlight', 'danger');
  const cancelButton = createControlButton('Cancel', 'Close');

  removeButton.addEventListener('click', () => {
    readingHighlightState.set(key, highlights.filter((item) => item.id !== highlightId));
    closeReadingFloatingControls();
    const container = getReadingPassageContainer();
    if (container) renderReadingHighlights(container, key);
  });

  cancelButton.addEventListener('click', closeReadingFloatingControls);
  actions.append(cancelButton, removeButton);
  popover.append(text, actions);
  document.body.appendChild(popover);
}

function ensureReadingPassageNotes() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  window.setTimeout(() => {
    const container = getReadingPassageContainer();
    if (!container) return;

    const key = getReadingHighlightKey(container);
    renderReadingHighlights(container, key);

    if (readingHighlightController) return;

    readingHighlightController = new AbortController();
    const { signal } = readingHighlightController;

    document.addEventListener('mouseup', () => {
      const currentContainer = getReadingPassageContainer();
      const selection = window.getSelection();
      if (!currentContainer || !selection || selection.isCollapsed || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (!currentContainer.contains(range.commonAncestorContainer)) return;

      const text = selection.toString().trim();
      if (!text) return;

      const offsets = getRangeOffsets(currentContainer, range);
      if (!offsets) return;

      pendingReadingSelection = { ...offsets, text };
      suppressReadingDocumentClicksUntil = Date.now() + 600;
      showSelectionToolbar(range.getBoundingClientRect());
    }, { signal });

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (target?.closest?.('#ielts-reading-selection-toolbar') || target?.closest?.('#ielts-reading-note-popover')) {
        return;
      }

      if (Date.now() < suppressReadingDocumentClicksUntil) {
        return;
      }

      if (target?.classList?.contains(READING_HIGHLIGHT_CLASS)) {
        const currentContainer = getReadingPassageContainer();
        if (!currentContainer) return;
        const currentKey = getReadingHighlightKey(currentContainer);
        const highlights = readingHighlightState.get(currentKey) || [];
        const highlight = highlights.find((item) => item.id === target.dataset.highlightId);
        if (highlight?.type === 'note') {
          showReadingNotePopover(target, currentKey, target.dataset.highlightId);
        } else {
          showMarkerOptionsPopover(target, currentKey, target.dataset.highlightId);
        }
        return;
      }

      closeReadingFloatingControls();
      pendingReadingSelection = null;
    }, { signal });
  }, 0);
}

/**
 * Extract blank-to-number mapping and clean HTML
 */
export function extractBlankNumberMappingAndCleanHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blankToNumber = {};
  const selectionToNumber = {};

  doc.querySelectorAll('p').forEach(p => {
    const pHTML = p.innerHTML;
    const markers = [];
    const blanks = [];

    const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
    let markerMatch;
    while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
      markers.push({
        number: markerMatch[2] || markerMatch[3],
        index: markerMatch.index
      });
    }

    p.querySelectorAll('.tiptap-input-field').forEach(span => {
      const inTable = span.closest('table');
      const inQuestionBoxContent = span.closest('.question-box-content');
      if (!inTable || inQuestionBoxContent) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          blanks.push({ span, index: spanIndex });
        }
      }
    });

    const selections = [];
    p.querySelectorAll('.selection-field').forEach(span => {
      const inTable = span.closest('table');
      const inQuestionBoxContent = span.closest('.question-box-content');
      if (!inTable || inQuestionBoxContent) {
        const spanHTML = span.outerHTML;
        const spanIndex = pHTML.indexOf(spanHTML);
        if (spanIndex !== -1) {
          selections.push({ span, index: spanIndex });
        }
      }
    });

    blanks.forEach(blank => {
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < blank.index) {
          matchedMarker = markers[i];
          break;
        }
      }

      if (matchedMarker && blank.span && blank.span.id) {
        blankToNumber[blank.span.id] = matchedMarker.number;
      }
    });

    selections.forEach(selection => {
      let matchedMarker = null;
      for (let i = markers.length - 1; i >= 0; i--) {
        if (markers[i].index < selection.index) {
          matchedMarker = markers[i];
          break;
        }
      }
      if (matchedMarker && selection.span && selection.span.id) {
        selectionToNumber[selection.span.id] = matchedMarker.number;
      }
    });
  });

  doc.querySelectorAll('.question-box-content').forEach(boxContent => {
    if (boxContent.innerHTML) {
      boxContent.innerHTML = cleanAngleBrackets(boxContent.innerHTML);
    }
  });

  doc.querySelectorAll('p').forEach(p => {
    p.innerHTML = p.innerHTML.replace(/(&lt;\d+&gt;|<\d+>)/g, '');
  });

  return {
    cleanedHtml: doc.body.innerHTML,
    blankToNumber,
    selectionToNumber
  };
}

/**
 * Process passage text to replace blanks with placeholders for hydration
 */
export function processPassageText(html) {
  ensureReadingPassageNotes();

  return html.replace(
    /<span class=\"tiptap-input-field\" id=\"(blank-[^\"]+)\">.*?<\/span>/g,
    (match, id) => `<span data-blank-id="${id}"></span>`
  );
}
